// Technical Specification API service functions

const API_BASE_URL = '/api';

// Get all technical specifications for a project
export const getTechnicalSpecifications = async (projectId, allVersions = false) => {
  try {
    const params = new URLSearchParams();
    if (allVersions) {
      params.append('all_versions', 'true');
    }
    
    const response = await fetch(
      `${API_BASE_URL}/projects/${projectId}/technical-specifications?${params}`, 
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching technical specifications:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get a specific technical specification
export const getTechnicalSpecification = async (projectId, techSpecId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/projects/${projectId}/technical-specifications/${techSpecId}`, 
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching technical specification:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Create a new technical specification
export const createTechnicalSpecification = async (projectId, data) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/projects/${projectId}/technical-specifications`, 
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data)
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating technical specification:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Update a specific section of a technical specification
export const updateTechnicalSpecificationSection = async (projectId, techSpecId, sectionName, data) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/projects/${projectId}/technical-specifications/${techSpecId}/sections/${sectionName}`, 
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data)
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error updating technical specification section:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Generate AI content for a technical specification section
export const generateTechnicalSpecificationContent = async (projectId, techSpecId, section, customInstructions = '') => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/projects/${projectId}/technical-specifications/${techSpecId}/generate-content`, 
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          section,
          custom_instructions: customInstructions
        })
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error generating technical specification content:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Create a new version of a technical specification
export const createTechnicalSpecificationVersion = async (projectId, techSpecId, data = {}) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/projects/${projectId}/technical-specifications/${techSpecId}/versions`, 
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data)
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error creating technical specification version:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Export technical specification to various formats
export const exportTechnicalSpecification = async (projectId, techSpecId, format = 'pdf') => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/projects/${projectId}/technical-specifications/${techSpecId}/export`, 
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ format })
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error exporting technical specification:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Update technical specification status
export const updateTechnicalSpecificationStatus = async (projectId, techSpecId, statusData) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/projects/${projectId}/technical-specifications/${techSpecId}/status`, 
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(statusData)
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error updating technical specification status:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Delete a technical specification
export const deleteTechnicalSpecification = async (projectId, techSpecId) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/projects/${projectId}/technical-specifications/${techSpecId}`, 
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error deleting technical specification:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Generate technical specification document with full workflow (similar to functional specs)
export const generateTechnicalSpecificationDocument = async (formData) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/technical-specifications/generate`, 
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error generating technical specification document:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Generate enhanced technical specification document with SOW integration
export const generateTechnicalSpecificationDocumentEnhanced = async (formData) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/technical-specifications/generate-enhanced`, 
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error generating enhanced technical specification document:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Generate all sections with enhanced AI processing
export const generateAllTechnicalSpecificationSections = async (projectId, techSpecId, customInstructions = '') => {
  const sections = [
    'system_architecture',
    'technical_design', 
    'integration_specs',
    'security_specs',
    'performance_specs',
    'deployment_specs',
    'custom_development'
  ];
  
  const results = {
    success: true,
    sections: {},
    errors: []
  };
  
  try {
    for (const section of sections) {
      const result = await generateTechnicalSpecificationContent(
        projectId, 
        techSpecId, 
        section, 
        customInstructions
      );
      
      if (result.success) {
        results.sections[section] = result;
      } else {
        results.errors.push({
          section,
          error: result.error
        });
      }
      
      // Small delay between requests to avoid overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    // If any errors occurred, mark as partial success
    if (results.errors.length > 0) {
      results.success = false;
      results.message = `Generated ${Object.keys(results.sections).length} of ${sections.length} sections successfully`;
    } else {
      results.message = 'All sections generated successfully';
    }
    
    return results;
  } catch (error) {
    console.error('Error generating all technical specification sections:', error);
    return {
      success: false,
      error: error.message,
      sections: results.sections,
      errors: results.errors
    };
  }
};

// Utility function to get section completion status
export const getTechnicalSpecificationCompletionStatus = (technicalSpec) => {
  if (!technicalSpec) return {};
  
  const sections = [
    'system_architecture',
    'technical_design',
    'integration_specs', 
    'security_specs',
    'performance_specs',
    'deployment_specs',
    'custom_development'
  ];
  
  const completion = {};
  let totalSections = sections.length;
  let completedSections = 0;
  
  sections.forEach(section => {
    const isCompleted = technicalSpec.completion_status && technicalSpec.completion_status[section];
    completion[section] = isCompleted;
    if (isCompleted) completedSections++;
  });
  
  completion.percentage = Math.round((completedSections / totalSections) * 100);
  completion.completed = completedSections;
  completion.total = totalSections;
  
  return completion;
};

export default {
  getTechnicalSpecifications,
  getTechnicalSpecification,
  createTechnicalSpecification,
  updateTechnicalSpecificationSection,
  generateTechnicalSpecificationContent,
  generateTechnicalSpecificationDocument,
  generateTechnicalSpecificationDocumentEnhanced,
  createTechnicalSpecificationVersion,
  exportTechnicalSpecification,
  updateTechnicalSpecificationStatus,
  deleteTechnicalSpecification,
  generateAllTechnicalSpecificationSections,
  getTechnicalSpecificationCompletionStatus
};